import { useState } from "react";
import { Link, useParams } from "react-router";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  useAddLookup,
  useDeleteLookup,
  useLookupCatalog,
  useLookupRows,
  useUpdateLookup,
} from "src/dm/hooks/useAdminLookups";
import { LookupRow } from "src/dm/interfaces/AdminLookup";

const friendlyName = (key: string) =>
  key
    .split("-")
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");

const AdminLookupPage = () => {
  const { name } = useParams<{ name: string }>();
  const catalog = useLookupCatalog();
  const def = catalog.data?.find((d) => d.key === name);
  const { data, isLoading, isError, error } = useLookupRows(name);
  const add = useAddLookup(name ?? "");
  const update = useUpdateLookup(name ?? "");
  const remove = useDeleteLookup(name ?? "");

  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState<LookupRow>({ id: "", description: "" });
  const [editRow, setEditRow] = useState<LookupRow | null>(null);
  // audit/22-screenshot-inventory.md §4 MINOR #8 — Delete was using the
  // native browser `confirm()`, which is unstyled and dismisses on a stray
  // Enter keypress. Replace with a Kendo Dialog for consistency with the
  // Add / Edit dialogs already in this page. State holds the row pending
  // delete; closed = null.
  const [pendingDelete, setPendingDelete] = useState<LookupRow | null>(null);
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
    } finally {
      setPendingDelete(null);
    }
  };

  const handleAddSubmit = async () => {
    if (!draft.id.trim() || !draft.description.trim()) return;
    await add.mutateAsync(draft);
    setShowAdd(false);
    setDraft({ id: "", description: "" });
  };

  const handleEditSubmit = async () => {
    if (!editRow) return;
    if (!editRow.description.trim()) return;
    await update.mutateAsync(editRow);
    setEditRow(null);
  };

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as LookupRow;
    // Disable Delete when the row is referenced by other tables — mirrors
    // legacy "Cells[2] != 0 → disable delete icon" (audit 14 §4 / audit 22
    // §MAJOR). Lookups without an observable referrer always report 0.
    const inUse = (row.usageCount ?? 0) > 0;
    return (
      <td>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          onClick={() => setEditRow({ ...row })}
        >
          Edit
        </Button>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          themeColor="error"
          onClick={() => setPendingDelete(row)}
          disabled={remove.isPending || inUse}
          title={
            inUse
              ? `Cannot delete — referenced by ${row.usageCount} active row(s)`
              : undefined
          }
        >
          Delete
        </Button>
      </td>
    );
  };

  if (!name) return <p>Pick a lookup from the Admin landing.</p>;

  return (
    <div>
      <Link
        to="/dm/admin"
        style={{
          display: "inline-block",
          marginBottom: "0.5rem",
          fontSize: "0.85rem",
          color: "#0a3d62",
          textDecoration: "none",
        }}
      >
        ← Admin
      </Link>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.75rem",
        }}
      >
        <h1 style={{ margin: 0 }}>{friendlyName(name)}</h1>
        <Button type="button" themeColor="primary" onClick={() => setShowAdd(true)}>
          Add row
        </Button>
      </div>
      {def && (
        <p style={{ color: "#64748b", fontSize: "0.85rem", marginTop: 0 }}>
          {def.table} — key column <code>{def.keyColumn}</code>, description{" "}
          <code>{def.descriptionColumn}</code>
        </p>
      )}

      {isLoading && <p>Loading…</p>}
      {isError && (
        <p style={{ color: "#b91c1c" }}>
          {error instanceof Error ? error.message : "Failed to load"}
        </p>
      )}

      {data && data.items.length === 0 && (
        <p style={{ color: "#94a3b8" }}>No rows.</p>
      )}

      {data && data.items.length > 0 && (
        <Grid data={data.items}>
          <GridColumn field="id" title="ID" width="180px" />
          <GridColumn field="description" title="Description" />
          <GridColumn field="usageCount" title="Used By" width="120px" />
          <GridColumn title="" width="160px" cells={{ data: ActionsCell }} />
        </Grid>
      )}

      {showAdd && (
        <Dialog title={`Add ${friendlyName(name)}`} onClose={() => setShowAdd(false)}>
          <label style={{ display: "block", marginBottom: "0.5rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>ID</span>
            <Input
              value={draft.id}
              onChange={(e) => setDraft({ ...draft, id: String(e.target.value ?? "") })}
            />
          </label>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>Description</span>
            <Input
              value={draft.description}
              onChange={(e) =>
                setDraft({ ...draft, description: String(e.target.value ?? "") })
              }
            />
          </label>
          {add.isError && (
            <pre style={{ color: "#b91c1c", fontSize: "0.8rem", margin: "0.5rem 0" }}>
              {add.error instanceof Error ? add.error.message : "Failed"}
            </pre>
          )}
          <DialogActionsBar>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              themeColor="primary"
              onClick={handleAddSubmit}
              disabled={add.isPending || !draft.id.trim() || !draft.description.trim()}
            >
              {add.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {editRow && (
        <Dialog title={`Edit ${editRow.id}`} onClose={() => setEditRow(null)}>
          <label style={{ display: "block", marginBottom: "0.5rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>ID</span>
            <Input value={editRow.id} disabled />
          </label>
          <label style={{ display: "block" }}>
            <span style={{ fontSize: "0.8rem", color: "#475569" }}>Description</span>
            <Input
              value={editRow.description}
              onChange={(e) =>
                setEditRow({ ...editRow, description: String(e.target.value ?? "") })
              }
            />
          </label>
          {update.isError && (
            <pre style={{ color: "#b91c1c", fontSize: "0.8rem", margin: "0.5rem 0" }}>
              {update.error instanceof Error ? update.error.message : "Failed"}
            </pre>
          )}
          <DialogActionsBar>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button
              themeColor="primary"
              onClick={handleEditSubmit}
              disabled={update.isPending}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {pendingDelete && (
        <Dialog
          title="Confirm delete"
          onClose={() => setPendingDelete(null)}
          width={420}
        >
          <p style={{ margin: "0.25rem 0 0.75rem" }}>
            Delete{" "}
            <code style={{ background: "#f1f5f9", padding: "0 0.25rem" }}>
              {pendingDelete.id}
            </code>{" "}
            ({pendingDelete.description})?
          </p>
          <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>
            This cannot be undone. Existing rows that reference this lookup
            will be left dangling.
          </p>
          {remove.isError && (
            <pre
              style={{
                color: "#b91c1c",
                fontSize: "0.8rem",
                margin: "0.5rem 0 0",
              }}
            >
              {remove.error instanceof Error
                ? remove.error.message
                : "Delete failed"}
            </pre>
          )}
          <DialogActionsBar>
            <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              themeColor="error"
              onClick={confirmDelete}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default AdminLookupPage;
